<script setup lang="ts">
import { storeToRefs } from 'pinia';

import AppLogo from '@clovyr/bed/components/elements/AppLogo.vue';
import {
  firstPartyHosts,
  getHostByID,
  thirdPartyHosts,
  wNextHost,
} from '@clovyr/pollen/fixtures/hosts';
import { type ComputeProviderConfig, ComputeProviderName } from '@clovyr/pollen/types/Host';

import { useLauncherStore } from '@/stores/launcher_store';
import { usePollenStore } from '@/stores/pollen_store';
import { LauncherState } from '@/stores/types';

import MarketingFeatureList from '../blocks/MarketingFeatureList.vue';

import HostSelectorBlock from './HostSelectorBlock.vue';
import SavedHostsDropdown from './SavedHostDropdown.vue';
import ServerOptionsDropdown from './ServerOptionsDropdown.vue';

const launcherStore = useLauncherStore();
const { selectedHost, status, wnextAppPrice } = storeToRefs(launcherStore);
const { setSelectedHost, setComputeProviderConfig } = launcherStore;

const isWNext = computed(() => selectedHost.value?.id === ComputeProviderName.WNext);

// Logos including clovyr hosting
const combinedHostLogos = computed(() => {
  return [...firstPartyHosts, ...thirdPartyHosts];
});

const { computeProviderConfigs } = storeToRefs(usePollenStore());
const selectedSavedHosts = ref(false);
const haveSavedHosts = computed(
  () => computeProviderConfigs.value && computeProviderConfigs.value.length > 0,
);

// Create a local state for radio selection that we can control
const localHostSelection = ref(
  selectedHost.value?.id === ComputeProviderName.WNext ? 'wnext' : 'third-party',
);

// Watch for changes in our local selection and update the store
watch(localHostSelection, (newSelection) => {
  if (newSelection === 'wnext') {
    setSelectedHost(wNextHost);
    selectedSavedHosts.value = false;
  } else if (newSelection === 'third-party') {
    setSelectedHost();
    selectedSavedHosts.value = false;
  }
});

const setSelectedOption = async (option: ComputeProviderConfig) => {
  selectedSavedHosts.value = true;
  const hostInfo = getHostByID(option.providerID);
  setSelectedHost(hostInfo);
  await setComputeProviderConfig(option);
};
</script>

<template>
  <div class="host-selector">
    <!-- Review screen (read-only) -->
    <template v-if="status === LauncherState.Review">
      <div>
        <div class="review-host-block options-block">
          <div class="review-host-block__top">
            <template v-if="!isWNext">
              <AppLogo
                :logo="selectedHost!"
                size="medium"
                :pad="
                  selectedHost?.id !== ComputeProviderName.DigitalOcean &&
                  selectedHost?.id !== ComputeProviderName.WNext
                "
              />
              <div class="heading">
                <div class="review-host-block-heading text--h4">{{ selectedHost?.title }}</div>
                <div class="review-host-block-subheading">{{ selectedHost?.subtitle }}</div>
              </div>
            </template>
            <template v-else>
              <ImageMedia :filename="selectedHost!.fullLogoUrl" />
            </template>
          </div>
          <div class="review-host-block__bottom">
            <p class="description" v-if="selectedHost?.id === ComputeProviderName.WNext">
              {{ selectedHost?.description?.replaceAll('PRICE', wnextAppPrice) }}
            </p>
            <p class="description" v-if="selectedHost?.id !== ComputeProviderName.WNext">
              {{ selectedHost?.description }}
            </p>
          </div>
          <div class="features-list">
            <MarketingFeatureList hide-logo truncate title-only />
          </div>

          <!-- Server Regions & Server Size Dropdown -->
          <div class="server-options">
            <ServerOptionsDropdown />
          </div>
        </div>
      </div>
    </template>

    <!-- Editing -->
    <template v-else>
      <!-- Clovyr Hosting -->
      <HostSelectorBlock
        v-if="!haveSavedHosts"
        class="hosting-block first-party"
        :logos="[]"
        :selected="localHostSelection === 'wnext'"
        @select="localHostSelection = 'wnext'"
      >
        <template v-slot:heading-right>
          <div class="free-trial">7 day free trial on us!</div>
          <ImageMedia :filename="wNextHost.fullLogoUrl" />
        </template>
        <template v-slot:body-left>
          {{ wNextHost.description?.replaceAll('PRICE', wnextAppPrice) }}
          <div class="features-list">
            <MarketingFeatureList hide-logo truncate title-only />
          </div>
        </template>
      </HostSelectorBlock>

      <!-- Saved hosts -->
      <SavedHostsDropdown
        v-else
        class="hosting-block host-dropdown"
        :selected="selectedSavedHosts"
        @selected="setSelectedOption($event)"
      />

      <!-- Other host options -->
      <HostSelectorBlock
        class="hosting-block third-party"
        :logos="haveSavedHosts ? combinedHostLogos : thirdPartyHosts"
        logoSize="small"
        :selected="localHostSelection === 'third-party'"
        @select="localHostSelection = 'third-party'"
      >
        <template v-slot:body-heading>Choose another host</template>
      </HostSelectorBlock>
    </template>
  </div>
</template>

<style lang="scss" scoped>
@use '@clovyr/bed/styles/vendor/bootstrap/scss/bootstrap';
@use '@clovyr/bed/styles/core';
@use 'src/styles/core/page';
@use 'src/styles/core/vertical-spacing';
@use 'styles/core/layout/grid-vars';
@use 'styles/core/responsive';
@use 'styles/core/typography/fonts';
@use 'styles/vendor/bootstrap/scss/mixins/breakpoints';

:deep(.radio-block__inner) {
  @include responsive.media-query(phone) {
    padding-left: grid-vars.space(2);
    padding-right: grid-vars.space(2);
  }

  @include responsive.media-query(tablet) {
    padding-top: grid-vars.space(3);
    padding-left: grid-vars.space(2);
    padding-right: grid-vars.space(2);
  }
}

:deep(.radio-block__right) {
  @include responsive.media-query(phone) {
    padding-bottom: grid-vars.space(2);
  }
  @include responsive.media-query(tablet) {
    padding-bottom: grid-vars.space(2);
  }
}

:deep(.block-heading__left) {
  @include responsive.media-query(phone) {
    //this converts column back to row
    //overriding some styles here
    flex-wrap: nowrap !important;

    .block-icon {
      margin: 2px !important;
    }
  }

  @include responsive.media-query(tablet) {
    //this converts column back to row
    //overriding some styles here
    flex-wrap: nowrap !important;

    .block-icon {
      margin: 2px !important;
    }
  }
}
.third-party {
  @include responsive.media-query(phone) {
    :deep(.radio-block__left) {
      transform: translateX(5px);
    }
  }

  @include responsive.media-query(tablet) {
    :deep(.radio-block__left) {
      transform: translateX(5px);
    }
  }
}

.first-party {
  @include responsive.media-query(phone) {
    :deep(.block-heading__left) {
      display: none;
    }
  }
  @include responsive.media-query(tablet) {
    :deep(.block-heading__left) {
      display: none;
    }
  }

  :deep(.radio-block__inner) {
    @include breakpoints.media-breakpoint-only(xs) {
      padding-top: grid-vars.space(4);
    }
    @include breakpoints.media-breakpoint-up(lg) {
      padding-top: grid-vars.space(4);
    }
  }
}

.free-trial {
  position: absolute;
  background-color: #383e52;
  color: #33e672;
  border-radius: 8px;
  z-index: 1;
  padding: grid-vars.space(1) grid-vars.space(3);
  transform: translateX(-50%);
  white-space: pre;
  top: 55px;
  left: 50%;

  @include responsive.media-query(phone-xs) {
    font-size: 9px;
  }
}
.review-host-block {
  &__top {
    display: flex;
    align-items: center;
    margin-bottom: grid-vars.space(2);

    @include fonts.font(work, regular, 24, 1.2);

    .review-host-block-heading {
      margin-left: grid-vars.space(2);
    }

    .review-host-block-subheading {
      margin-left: grid-vars.space(2);
      color: #33e672;
      font-size: 12px;
    }
  }

  &__bottom {
    :deep(.button) {
      width: 100%;
    }
  }

  &__button {
    margin: grid-vars.space(2) 0 0 0;
  }
}

.features-list {
  margin: 2rem 0;
}
</style>
